/**
 * Utility service for time calculations and element state management
 *
 * This module wraps the shared package functions and adds webapp-specific
 * extensions like countdown and elementsById.
 */

import {
  // Types
  type Element,
  type FlattenedElement,
  type ElementState,
  type UrgencyLevelName,
  type UrgencySettings,
  // Constants
  URGENCY_COLORS,
  DEFAULT_URGENCY_SETTINGS,
  // Time utilities
  parseTimeToSeconds,
  formatSecondsToTime,
  formatDuration as sharedFormatDuration,
  formatTo12Hour,
  formatMinutesSeconds,
  calculateExampleTimes,
  convertTo24HourFormat,
  // Progress/countdown utilities
  calculateRemainingSeconds,
  // Element utilities
  getElementState as sharedGetElementState,
  getElementTimingsFromElements,
  getUrgencyLevel,
  getUrgencyColor as sharedGetUrgencyColor,
} from '@thekwallace/backtime-shared';

// ============================================================================
// Types
// ============================================================================

/**
 * Extended element with webapp-specific fields
 */
export interface DisplayElement extends FlattenedElement {
  /** Countdown in seconds until element starts (0 if started) */
  countdown: number;
  /** Whether this element is anchored (has anchorOffset) */
  isAnchored: boolean;
}

/**
 * Result of getAllElements function
 */
export interface AllElementsResult {
  /** Array of display elements */
  elements: DisplayElement[];
  /** Lookup object by element ID */
  elementsById: Record<string, DisplayElement>;
}

// ============================================================================
// Time Format Conversion
// ============================================================================

/**
 * Convert 24-hour time string to 12-hour format
 * @param time24H - Time in HH:MM:SS format
 * @returns Time in H:MM:SS AM/PM format
 */
export function convertTo12HourFormat(time24H: string | null | undefined): string {
  if (!time24H) return '';
  const seconds = parseTimeToSeconds(time24H);
  if (seconds === 0 && time24H !== '00:00:00' && time24H !== '0:00:00') {
    // parseTimeToSeconds returns 0 for invalid input, return original
    return time24H;
  }
  return formatTo12Hour(seconds, true);
}

// convertTo24HourFormat is imported from shared package

/**
 * Convert time string to seconds since midnight
 * Alias for parseTimeToSeconds from shared package
 */
export const convertToSeconds = parseTimeToSeconds;

/**
 * Convert seconds to HH:MM:SS time string
 * Alias for formatSecondsToTime from shared package
 */
export const convertSecondsToTime = formatSecondsToTime;

// ============================================================================
// Time Formatting
// ============================================================================

/**
 * Format countdown time in a human-readable format (H:MM:SS or M:SS)
 * Handles null/NaN with '--:--' fallback
 */
export function formatCountdown(totalSeconds: number | null | undefined): string {
  if (
    totalSeconds === null ||
    totalSeconds === undefined ||
    isNaN(totalSeconds)
  ) {
    return '--:--';
  }
  if (totalSeconds <= 0) return '0:00';
  return sharedFormatDuration(totalSeconds);
}

/**
 * Format duration in M:SS or H:MM:SS format
 * Alias for formatDuration from shared package
 */
export const formatDuration = sharedFormatDuration;

// formatMinutesSeconds and calculateExampleTimes are imported from shared package

// ============================================================================
// Day-Aware Time Calculations
// ============================================================================

// ============================================================================
// Element Calculations (Day-Aware)
// ============================================================================

/**
 * Get all elements with their timing information using absolute seconds
 * Uses shared getElementTimingsFromElements and adds webapp-specific fields
 *
 * @param currentAbsolute - Current time in absolute seconds
 * @param serviceStartAbsolute - Service start time in absolute seconds
 * @param elements - Array of Element objects (flat structure with sortOrder)
 * @returns Object with elements array and elementsById lookup
 */
export function getAllElements(
  currentAbsolute: number,
  serviceStartAbsolute: number,
  elements: Element[] | null | undefined
): AllElementsResult {
  if (!elements || elements.length === 0) {
    return { elements: [], elementsById: {} };
  }

  // Use shared function for core timing calculations
  const baseElements = getElementTimingsFromElements(serviceStartAbsolute, elements);

  // Add webapp-specific fields
  const displayElements: DisplayElement[] = baseElements.map((el: FlattenedElement) => ({
    ...el,
    countdown: calculateRemainingSeconds(currentAbsolute, el.startTime),
    isAnchored: el.anchorOffset !== null,
  }));

  // Build elementsById lookup
  const elementsById: Record<string, DisplayElement> = {};
  displayElements.forEach((el) => {
    elementsById[el.id] = el;
  });

  return { elements: displayElements, elementsById };
}

/**
 * Get the next upcoming time element to display
 * Finds the first element that hasn't started yet (for countdown display)
 *
 * @param currentAbsolute - Current time in absolute seconds
 * @param serviceStartAbsolute - Service start time in absolute seconds
 * @param elements - Array of Element objects
 * @returns Next upcoming element or null if all have started
 */
export function getNextTimeElement(
  currentAbsolute: number,
  serviceStartAbsolute: number,
  elements: Element[] | null | undefined
): DisplayElement | null {
  if (!elements || elements.length === 0) return null;

  // Use shared function for core logic
  const baseElements = getElementTimingsFromElements(serviceStartAbsolute, elements);

  // Find the first element that hasn't started yet (upcoming only)
  const nextBase = baseElements.find((el) => el.startTime > currentAbsolute);

  if (!nextBase) return null;

  // Add webapp-specific fields
  return {
    ...nextBase,
    countdown: calculateRemainingSeconds(currentAbsolute, nextBase.startTime),
    isAnchored: nextBase.anchorOffset !== null,
  };
}

/**
 * @deprecated Use getNextTimeElement instead
 */
export const getNextTimeElementFromPhases = getNextTimeElement;

/**
 * Find the ID of the element with the smallest positive countdown
 * @param elements - Array of display elements
 * @returns ID of next element or null if no positive countdowns
 */
export function findNextElementId(
  elements: Array<{ id: string; countdown: number }>
): string | null {
  let minCountdown = Infinity;
  let nextId: string | null = null;

  elements.forEach((element) => {
    if (element.countdown > 0 && element.countdown < minCountdown) {
      minCountdown = element.countdown;
      nextId = element.id;
    }
  });

  return nextId;
}

/**
 * Determine the state of an element (3 states: expired, active, upcoming)
 * Uses shared getElementState with element timing properties
 */
export function getElementState(
  element: { startTime: number; endTime: number },
  currentAbsolute: number
): ElementState {
  return sharedGetElementState(element.startTime, element.endTime, currentAbsolute);
}

/**
 * Calculate urgency level based on countdown time
 * @param countdownSeconds - Time until element starts
 * @returns Urgency level: 'imminent', 'soon', 'moderate', or 'distant'
 */
export function getUrgency(countdownSeconds: number): UrgencyLevelName {
  return getUrgencyLevel(countdownSeconds);
}

/**
 * Get urgency color based on element state and countdown time
 * State takes precedence: active=blue, expired=gray, upcoming=countdown-based
 * @param state - Element state (active, expired, or upcoming)
 * @param countdownSeconds - Time until element starts (only used for upcoming)
 * @returns Hex color string
 */
export function getUrgencyColor(state: ElementState, countdownSeconds: number): string {
  return sharedGetUrgencyColor(state, countdownSeconds);
}

// ============================================================================
// Default Service Object (for backwards compatibility)
// ============================================================================

/**
 * Utility service object with all functions
 * @deprecated Use named exports instead
 */
const utilityService = {
  // Time format conversion
  convertTo12HourFormat,
  convertTo24HourFormat,
  convertToSeconds,
  convertSecondsToTime,

  // Time formatting
  formatCountdown,
  formatDuration,

  // Element calculations
  getAllElements,
  getNextTimeElement,
  getNextTimeElementFromPhases, // deprecated alias
  findNextElementId,
  getElementState,
  getUrgency,
  getUrgencyColor,

  // Constants (from shared package)
  defaultUrgencySettings: DEFAULT_URGENCY_SETTINGS,
  urgencyColors: URGENCY_COLORS,
};

export default utilityService;

// Re-export shared package utilities for convenience
export {
  URGENCY_COLORS,
  DEFAULT_URGENCY_SETTINGS,
  parseTimeToSeconds,
  formatSecondsToTime,
  formatTo12Hour,
  formatMinutesSeconds,
  calculateExampleTimes,
  convertTo24HourFormat,
  calculateRemainingSeconds,
  getUrgencyLevel,
};

export type { Element, FlattenedElement, ElementState, UrgencyLevelName, UrgencySettings };
